import React, { useState } from 'react';
import Image from 'next/image';
import axios from 'axios';

import { Box, Sheet, Modal, ModalClose, Input, Typography, Button, Tabs, TabList, Tab, TabPanel, Grid } from '@mui/joy';

import CircularProgress from '@mui/joy/CircularProgress';

/* Custom Hooks */
import useUploadFile from '@/utils/useUploadFile';

/* Icons */
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import { toast } from 'react-toastify';

const MediaUpload = ({ postId, openMediaModal, setOpenMediaModal, postMedia, setPostMedia, setCurrentMediaType, session }) => {
    const [tabValue, setTabValue] = useState('upload');
    const [imageResults, setImageResults] = useState([]);
    const [imageQuery, setImageQuery] = useState('');
    const [videoQuery, setVideoQuery] = useState('');
    const [videoResults, setVideoResults] = useState([]);
    const [gifQuery, setGifQuery] = useState('');
    const [gifResults, setGifResults] = useState([]);
    const [imageAiPrompt, setImageAiPrompt] = useState('');
    const [generatedAiImage, setGeneratedAiImage] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [uploadErrorMessage, setUploadErrorMessage] = useState('');

    const { file, setFile, uploadFile } = useUploadFile();

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    }

    const handleSearchImages = async () => {
        setIsLoading(true);
        const query = imageQuery;

        const endpoint = `/api/media/pexels_image?query=${query}&per_page=80`;

        try {
            const response = await axios.get(endpoint);
            const results = response.data.photos;

            setImageResults(results);
        } catch (error) {
            console.error('Error fetching images:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearchVideo = async () => {
        setIsLoading(true);

        const query = videoQuery;

        const endpoint = `/api/media/pexels_video?query=${query}&per_page=80`;

        try {
            const response = await axios.get(endpoint);
            const results = response.data.videos;

            setVideoResults(results);
        } catch (error) {
            console.error('Error fetching videos:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearchGiphy = async () => {
        setIsLoading(true);

        const query = gifQuery;
        const endpoint = `/api/media/giphy?query=${query}&limit=10`;

        try {
            const response = await axios.get(endpoint);
            const gifData = response.data.data;

            setGifResults(gifData);
        } catch (error) {
            console.error('Error fetching GIFs from Giphy:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleImageAI = async (prompt) => {
        setIsLoading(true);

        try {
            const response = await fetch('/api/posts/image_generation', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ message: prompt }),
            });

            if (response.status != 200) {
                toast.error('Error generating image');
                setIsLoading(false);
                return;
            }

            const data = await response.json();
            const message = data.response;
            setGeneratedAiImage(message);
        } catch (error) {
            toast.error('Error generating image');
            console.error('Error generating image:', error);
        } finally {
            setIsLoading(false);
        }
    }

    const handleUploadFile = async (targetFile) => {

        // validate file type has a type and if so split it
        if (targetFile.type) {
            const fileType = targetFile.type.split('/')[1];

            // only allow jpg, png, gif, mp4, pdf
            if (fileType !== 'jpg' && fileType !== 'jpeg' && fileType !== 'png' && fileType !== 'gif' && fileType !== 'mp4' && fileType !== 'pdf') {
                setUploadErrorMessage('Only jpg, png, gif, mp4, pdf files are allowed.');
                setIsUploading(false);

                return;
            }

            // validate that only allow to upload one video or one pdf or multiple photos
            if (fileType === 'mp4' && (postMedia.photos.length > 0 || postMedia.pdf !== '')) {
                setUploadErrorMessage('You can only upload on your post a video or a document or photos, not both.');
                setIsUploading(false);

                return;
            }

            if (fileType === 'pdf' && (postMedia.photos.length > 0 || postMedia.video !== '')) {
                setUploadErrorMessage('You can only upload on your post a video or a document or photos, not both.');
                setIsUploading(false);

                return;
            }

            if ((fileType === 'jpg' || fileType === 'jpeg' || fileType === 'png' || fileType === 'gif') && (postMedia.video !== '' || postMedia.pdf !== '')) {
                setUploadErrorMessage('You can only upload on your post a video or a document or photos, not both.');
                setIsUploading(false);

                return;

            }
        }

        try {
            setIsUploading(true);

            const result = await uploadFile(postId, null, targetFile);

            if (result.success) {
                switch (result.fileType) {
                    case 'image':
                        setPostMedia(prevState => ({
                            ...prevState,
                            photos: [...prevState.photos, result.url]
                        }));
                        setCurrentMediaType('image');
                        setOpenMediaModal(false);
                        break;
                    case 'video':
                        setPostMedia(prevState => ({ ...prevState, video: result.url }));
                        setCurrentMediaType('video');
                        setOpenMediaModal(false);
                        break;
                    case 'application':
                        setPostMedia(prevState => ({ ...prevState, pdf: result.url }));
                        setCurrentMediaType('pdf');
                        setOpenMediaModal(false);
                        break;
                    default:
                        break;
                }
            }
        } catch (error) {
            console.error('Error uploading file:', error);
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <Modal
            aria-labelledby="modal-title"
            aria-describedby="modal-desc"
            open={openMediaModal}
            onClose={() => setOpenMediaModal(false)}
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}
        >
            <Sheet
                variant="outlined"
                sx={{
                    width: '100%',
                    height: '80vh',
                    maxWidth: 600,
                    borderRadius: 'md',
                    p: 0,
                    position: 'relative',
                    boxShadow: 'lg',
                }}
            >
                {isUploading && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.7)', // Semi-transparent white
                        zIndex: 1000, // Ensure it's above other content
                    }}>
                        <CircularProgress variant="solid" />
                        <Typography level="title-md" sx={{ marginLeft: '1rem' }}>Uploading...</Typography>
                    </div>
                )}

                <Typography level="h3" sx={{ marginBottom: '1rem', p: 2 }}>Add Media</Typography>

                <ModalClose
                    variant="plain"
                    sx={{
                        m: 1,
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        zIndex: 1100 // Higher than the tabs
                    }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Tabs value={tabValue} onChange={handleTabChange} sx={{ width: '100%' }}>
                        <TabList>
                            <Tab value="upload">Upload</Tab>
                            <Tab value="library">Photo Library</Tab>
                            <Tab value="video_library">Video Library</Tab>
                            <Tab value="giphy">Gif Library</Tab>
                            <Tab value="ai_image">AI Image</Tab>
                        </TabList>
                        <TabPanel value="upload">
                            {uploadErrorMessage && (
                                <Box sx={{ marginTop: '2rem', border: '1px dashed red', padding: '1rem' }}>
                                    <Typography level="title-md" sx={{ textAlign: 'center' }} color="danger">
                                        {uploadErrorMessage}
                                    </Typography>
                                </Box>
                            )}
                            <Box sx={{ marginTop: '1rem' }}>
                                <Typography level="h4" sx={{ marginBottom: '1rem' }}>Add an image, video or document</Typography>
                                <label htmlFor="raised-button-file" style={{ cursor: 'pointer' }}>
                                    <Box sx={{
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        backgroundColor: '#ebebef',
                                        padding: '2rem',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        textAlign: 'center',
                                    }}>
                                        <DriveFolderUploadIcon sx={{ fontSize: '3rem', color: 'primary.main' }} />
                                        <Typography variant="body1">Drag and drop or click to <u>upload</u></Typography>
                                    </Box>
                                </label>
                                <input
                                    accept="image/*,video/*,.pdf"
                                    style={{ display: 'none' }}
                                    id="raised-button-file"
                                    multiple
                                    type="file"
                                    onChange={(e) => handleUploadFile(e.target.files[0])}
                                />
                            </Box>



                        </TabPanel>

                        <TabPanel value="library">
                            {postMedia.video === '' && postMedia.pdf === '' ? (
                                <>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', marginTop: 0, padding: '10px 8px' }}>
                                        <Input
                                            onChange={(e) => setImageQuery(e.target.value)}
                                            placeholder="Search Photo Library"
                                            sx={{ width: '100%', marginBottom: 2, padding: 1 }}
                                            endDecorator={<Button
                                                size="lg"
                                                sx={{ padding: '10px 20px', marginRight: '5px', borderRadius: '5px' }}
                                                onClick={() => handleSearchImages()}

                                            >
                                                {isLoading ? <CircularProgress variant="solid" /> : "Search"}
                                            </Button>}
                                        />
                                    </Box>

                                    <Grid container spacing={3} sx={{ padding: '10px 8px', maxHeight: '50vh', overflowY: 'auto' }}>
                                        {imageResults?.map((photo) => (
                                            <Grid key={photo.id} xs={12} sm={6} md={4}>
                                                <Box
                                                    sx={{
                                                        overflow: 'hidden',
                                                        borderRadius: '5px',
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        position: 'relative',
                                                        width: '100%', // Full width of the grid item
                                                        paddingTop: '55%', // Aspect ratio of the image (height is 55% of the width for a roughly 16:9 ratio)
                                                        "&:before": {
                                                            content: '""',
                                                            display: 'block',
                                                            paddingTop: '100%',
                                                        },
                                                    }}
                                                    onClick={() => handleUploadFile(photo.src.large)}
                                                >
                                                    <img
                                                        src={photo.src.medium}
                                                        alt={`${photo.src.medium} photo`}
                                                        style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            bottom: 0,
                                                            left: 0,
                                                            right: 0,
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                            borderRadius: '5px',
                                                            cursor: 'pointer',
                                                        }}
                                                    />

                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>

                                </>
                            ) : (
                                <Box sx={{ marginTop: '2rem', border: '1px dashed #E4E4E4', padding: '1rem' }}>
                                    <Typography level="title-md" sx={{ textAlign: 'center' }}>
                                        You can only upload on your post a video or a document or photos, not both.
                                    </Typography>
                                    <Typography level="body-sm" sx={{ textAlign: 'center' }}>
                                        Please remove the video or the document to upload a photo.
                                    </Typography>
                                </Box>
                            )}

                        </TabPanel>

                        <TabPanel value="video_library">
                            {postMedia.photos.length < 1 && postMedia.pdf === '' ? (
                                <>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', marginTop: 0, padding: '10px 8px' }}>
                                        <Input
                                            onChange={(e) => setVideoQuery(e.target.value)}
                                            placeholder="Search Video Library"
                                            sx={{ width: '100%', marginBottom: 2, padding: 1 }}
                                            disabled={postMedia.photos.length > 0 || postMedia.pdf != ''}
                                            endDecorator={<Button
                                                size="lg"
                                                onClick={() => handleSearchVideo()}
                                                sx={{ padding: '10px 20px', marginRight: '5px', borderRadius: '5px' }}

                                            >
                                                {isLoading ? <CircularProgress variant="solid" /> : "Search"}
                                            </Button>}
                                        />
                                    </Box>

                                    <Grid container spacing={3} sx={{ padding: '10px 8px', maxHeight: '50vh', overflowY: 'auto' }}>
                                        {videoResults.map((video) => (
                                            <Grid key={video.id} xs={12} sm={6} md={4}>
                                                <Box
                                                    sx={{
                                                        overflow: 'hidden',
                                                        borderRadius: '5px',
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        position: 'relative',
                                                        width: '100%', // Full width of the grid item
                                                        paddingTop: '55%', // Aspect ratio of the image (height is 55% of the width for a roughly 16:9 ratio)
                                                        "&:before": {
                                                            content: '""',
                                                            display: 'block',
                                                            paddingTop: '100%',
                                                        },
                                                    }}
                                                    onClick={() => handleUploadFile(video.video_files[0].link)}
                                                >
                                                    <img
                                                        src={video.image}
                                                        alt="video"
                                                        style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            bottom: 0,
                                                            left: 0,
                                                            right: 0,
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                            borderRadius: '5px',
                                                            cursor: 'pointer',
                                                        }}
                                                    />
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>

                                    {/*
                                    <Box
                                        sx={{
                                            padding: '10px 8px',
                                            minHeight: '20vh',
                                            maxHeight: '35vh',
                                            border: '1px solid #E4E4E4',
                                            overflowY: 'scroll',
                                        }}>
                                        {videoResults.map((video) => (
                                            <Image
                                                key={video.id}
                                                src={video.image}
                                                alt="logo"
                                                width={150}
                                                height={150}
                                                sx={{
                                                    padding: '10px 10px',
                                                    border: '1px solid #E4E4E4',
                                                    margin: '5px',
                                                    borderRadius: '5px',
                                                    cursor: 'pointer'
                                                }}
                                                onClick={() => handleUploadFile(video.video_files[0].link)}
                                            />

                                        ))}
                                    </Box>
                                    */}
                                </>
                            ) : (
                                <Box sx={{ marginTop: '2rem', border: '1px dashed #E4E4E4', padding: '1rem' }}>
                                    <Typography level="title-md" sx={{ textAlign: 'center' }}>
                                        You can only upload on your post a video or a document or photos, not both.
                                    </Typography>
                                    <Typography level="body-sm" sx={{ textAlign: 'center' }}>
                                        Please remove the photos or the document to upload a video.
                                    </Typography>
                                </Box>
                            )}
                        </TabPanel>

                        <TabPanel value="giphy">
                            {postMedia.video === '' && postMedia.pdf === '' ? (
                                <>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', marginTop: 0, padding: '10px 0px' }}>
                                        <Input
                                            placeholder="Search Gif Library"
                                            sx={{ width: '100%', marginBottom: 0, padding: 1 }}
                                            onChange={(e) => setGifQuery(e.target.value)}
                                            endDecorator={<Button
                                                size="lg"
                                                sx={{ padding: '10px 20px', marginRight: '5px', borderRadius: '5px' }}
                                                onClick={() => handleSearchGiphy()}
                                            >
                                                {isLoading ? <CircularProgress variant="solid" /> : "Search"}
                                            </Button>}
                                        />

                                    </Box>

                                    <Typography level="body-sm" sx={{ marginBottom: '1rem' }}>
                                        Powered by <a href="https://giphy.com/" target="_blank" rel="noopener noreferrer">Giphy</a>
                                    </Typography>

                                    <Grid container spacing={3} sx={{ padding: '10px 8px', maxHeight: '50vh', overflowY: 'auto' }}>
                                        {gifResults.map((gif) => (
                                            <Grid key={gif.id} xs={12} sm={6} md={4}>
                                                <Box
                                                    sx={{
                                                        overflow: 'hidden',
                                                        borderRadius: '5px',
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        position: 'relative',
                                                        width: '100%', // Full width of the grid item
                                                        paddingTop: '55%', // Aspect ratio of the image (height is 55% of the width for a roughly 16:9 ratio)
                                                        "&:before": {
                                                            content: '""',
                                                            display: 'block',
                                                            paddingTop: '100%',
                                                        },
                                                    }}
                                                    onClick={() => handleUploadFile(gif.images.fixed_height.url)}
                                                >
                                                    <img
                                                        src={gif.images.fixed_height.url}
                                                        alt='gif'
                                                        style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            bottom: 0,
                                                            left: 0,
                                                            right: 0,
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                            borderRadius: '5px',
                                                            cursor: 'pointer',
                                                        }}
                                                    />
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>

                                </>
                            ) : (
                                <Box sx={{ marginTop: '2rem', border: '1px dashed #E4E4E4', padding: '1rem' }}>
                                    <Typography level="title-md" sx={{ textAlign: 'center' }}>
                                        You can only upload on your post a video or a document or photos, not both.
                                    </Typography>
                                    <Typography level="body-sm" sx={{ textAlign: 'center' }}>
                                        Please remove the video or the document to upload a GIF.
                                    </Typography>
                                </Box>
                            )}
                        </TabPanel>

                        <TabPanel value="ai_image">
                            {postMedia.video === '' && postMedia.pdf === '' ? (
                                <>
                                    {session.settings.ai_image_usage >= session.settings.ai_total_images ? (
                                        <Box sx={{ marginTop: '2rem', border: '3px dashed #9C27B0', padding: '1rem' }}>
                                            <Typography level="title-md" sx={{ textAlign: 'center' }}>
                                                You have reached the maximum number of image generations
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', marginTop: 0, padding: '10px 8px' }}>
                                            <Input
                                                placeholder="people walking in the park"
                                                sx={{ width: '100%', marginBottom: 2, padding: 1 }}
                                                onChange={(e) => setImageAiPrompt(e.target.value)}
                                                disabled={isLoading}
                                                endDecorator={<Button
                                                    size="lg"
                                                    sx={{ padding: '10px 20px', marginRight: '5px', borderRadius: '5px' }}
                                                    onClick={() => handleImageAI(imageAiPrompt)}
                                                >
                                                    {isLoading ? <CircularProgress variant="solid" /> : "Generate"}
                                                </Button>}
                                            />
                                        </Box>
                                    )}

                                    {generatedAiImage && (
                                        <>
                                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                <Image
                                                    src={generatedAiImage}
                                                    alt="generated image"
                                                    width={300}
                                                    height={300}
                                                    onClick={() => handleUploadFile(generatedAiImage)}
                                                    style={{
                                                        padding: '10px 10px',
                                                        border: '1px solid #E4E4E4',
                                                        margin: '5px',
                                                        borderRadius: '5px',
                                                        cursor: 'pointer'
                                                    }}
                                                />
                                            </Box>
                                        </>
                                    )}
                                </>
                            ) : (
                                <Box sx={{ marginTop: '2rem', border: '1px dashed #E4E4E4', padding: '1rem' }}>
                                    <Typography level="title-md" sx={{ textAlign: 'center' }}>
                                        You can only upload on your post a video or a document or photos, not both.
                                    </Typography>
                                    <Typography level="body-sm" sx={{ textAlign: 'center' }}>
                                        Please remove the video or the document to upload an image.
                                    </Typography>
                                </Box>
                            )}
                        </TabPanel>
                    </Tabs>
                </Box>

            </Sheet>
        </Modal >
    );
}

export default MediaUpload;
